import React, { useEffect, useState } from 'react';
import {
  X, Moon, Sun, Settings as SettingsIcon, UserCircle, Bell, ShieldCheck,
  SlidersHorizontal, CreditCard,
} from 'lucide-react';
import { useThemeMode } from '../../context/ThemeContext';
import { useSettingsModal } from '../../context/SettingsModalContext';
import SubscriptionSection from './SubscriptionSection';

// Settings modal (docs/DECISIONS.md, "Settings modal" decision) - replaces
// the old full-page /settings route. Backdrop/panel/close-button styling
// adapted from LoginModal.jsx (src/components/Landing/LoginModal.jsx), the
// only other modal precedent in this app - widened to fit a left-nav +
// content layout, plus escape-key and backdrop-click close, neither of
// which LoginModal.jsx has.
const SECTIONS = [
  { key: 'general', label: 'General', icon: SettingsIcon },
  { key: 'account', label: 'Account', icon: UserCircle },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'privacy', label: 'Privacy', icon: ShieldCheck },
  { key: 'preferences', label: 'Preferences', icon: SlidersHorizontal },
  { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
];

function PlaceholderSection({ label }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6 space-y-3">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue">
        <SettingsIcon size={22} />
      </span>
      <p className="text-sm font-semibold text-navy">{label}</p>
      <p className="text-sm text-text-slate max-w-xs">We're still building this out - check back soon.</p>
      <span className="inline-flex items-center rounded-full bg-surface-light px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-blue">
        Coming soon
      </span>
    </div>
  );
}

function GeneralSection() {
  const { isDarkTheme, toggleTheme } = useThemeMode();
  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="rounded-card border border-gray-200 p-4 sm:p-5">
        {/* flex-col below sm: - the row version forced the button onto the
            same line as the description text with a flex-shrink-0 button,
            which had nowhere to go but overflow past the modal's edge on
            a phone width (reported live on a real device, docs/
            DECISIONS.md, "Settings modal: Appearance row overflow on
            mobile" decision). Unchanged at sm: and up. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <p className="text-sm font-semibold text-navy">Appearance</p>
            <p className="text-xs text-text-slate mt-0.5">Switch between light and dark mode across the app.</p>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-btn text-sm font-semibold bg-surface-light hover:bg-gray-200 text-navy transition-colors flex-shrink-0 w-full sm:w-auto"
          >
            {isDarkTheme ? <Sun size={16} className="text-brand-blue" /> : <Moon size={16} className="text-brand-blue" />}
            {isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SettingsModal() {
  const { isOpen, closeSettings } = useSettingsModal();
  const [activeSection, setActiveSection] = useState('general');

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeSettings();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeSettings]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed top-0 right-0 bottom-0 left-0 z-[10001] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={closeSettings}
    >
      <div
        className="bg-white border border-gray-200 rounded-dialog shadow-brand-lg relative w-full max-w-3xl h-[32rem] max-h-[90vh] flex overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={closeSettings}
          className="absolute top-4 right-4 text-text-slate hover:text-navy transition-colors z-10"
          aria-label="Close settings dialog"
        >
          <X size={22} />
        </button>

        {/* Stays a left-side vertical column at every width, per explicit
            user request (a horizontal top strip needed side-scrolling to
            see every section, which was worse) - narrows to icon-only
            below sm: (labels hidden) so it and the content area both fit
            on a phone without any scrolling (docs/DECISIONS.md, "Settings
            modal: left-side menu on mobile, not a horizontal strip"
            decision). Unchanged at sm: and up. */}
        <div className="w-14 sm:w-48 flex-shrink-0 border-r border-gray-200 bg-surface-light py-5 px-1.5 sm:px-2 space-y-1 overflow-y-auto">
          <h2 className="hidden sm:block px-3 pb-3 text-sm font-display font-bold text-navy">Settings</h2>
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.key;
            return (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveSection(section.key)}
                title={section.label}
                className={`w-full flex items-center justify-center sm:justify-start gap-2.5 rounded-btn px-2 sm:px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-blue/10 text-brand-blue' : 'text-text-slate hover:bg-white hover:text-navy'
                }`}
              >
                <Icon size={16} className="flex-shrink-0" />
                <span className="hidden sm:inline">{section.label}</span>
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeSection === 'general' ? (
            <GeneralSection />
          ) : activeSection === 'subscriptions' ? (
            <SubscriptionSection />
          ) : (
            <PlaceholderSection label={SECTIONS.find((section) => section.key === activeSection)?.label} />
          )}
        </div>
      </div>
    </div>
  );
}
